import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { titleize, humanize } from 'underscore.string';
import { massUpdateActions } from 'constants/MassUpdates';
import { getUpdateFieldForAction } from 'helpers/MassUpdates';
import CargomaticInput from 'components/v2/input';
import { withTranslation } from 'react-i18next';
import { t } from 'i18next';
import { sluggify } from 'utils/localization/utils';
import ErrorAlert from '../../ui/ErrorAlert';
import TagEditor from 'components/ui/TagEditor';
import AdminConstants from 'constants/AdminConstants';

const defaultColumnHeaders = [
  { name: 'Shipment', localizationSlug: 'SHIPMENT', count: 1 },
  { name: 'Shipper', localizationSlug: 'SHIPPER' },
  { name: 'Equipment', localizationSlug: 'EQUIPMENT' },
];
const bulkClearTagsHeaders = [
  { name: 'Shipment', localizationSlug: 'SHIPMENT' },
  { name: 'Shipper', localizationSlug: 'SHIPPER' },
  { name: 'Tags', localizationSlug: 'TAGS' },
  { name: 'Remove', localizationSlug: 'REMOVE' },
];
const bulkAddTagsHeaders = [
  { name: 'Shipment', localizationSlug: 'SHIPMENT' },
  { name: 'Shipper', localizationSlug: 'SHIPPER' },
  { name: 'Tags', localizationSlug: 'TAGS' },
  { name: 'Bulk Add Tags', localizationSlug: 'BULK_ADD_TAGS' },
  { name: 'Apply To All', localizationSlug: 'APPLY_TO_ALL' },
  { name: 'Remove', localizationSlug: 'REMOVE' },
];
const disableApplyToAllForLast = [massUpdateActions.BULK_ADD_TAGS];

const hideClearAllActions = [massUpdateActions.BULK_CLEAR_TAGS];

class ShipmentsGroup extends Component {
  static propTypes = {
    updateAction: PropTypes.string.isRequired,
    shipmentUpdates: PropTypes.object,
    shipments: PropTypes.array.isRequired,
    shipmentUpdateResults: PropTypes.array,
    title: PropTypes.string,
    saving: PropTypes.bool,
    disableSave: PropTypes.bool,
    onClearAll: PropTypes.func.isRequired,
    onSaveAll: PropTypes.func.isRequired,
    onApplyToAll: PropTypes.func.isRequired,
    onFieldChange: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      notes: {},
    };
  }

  getActionName() {
    const { updateAction, t } = this.props;

    switch (updateAction) {
      case massUpdateActions.SHIPMENT_ARRIVED:
        return t('ARRIVE_ALL');
      case massUpdateActions.BULK_CLEAR_TAGS:
        return t('CLEAR_TAGS');
      default:
        return t('SAVE_ALL');
    }
  }

  getLocalisedHeaders(headers = []) {
    return headers?.map((header) =>
      header.count
        ? t(header.localizationSlug, { count: header.count })
        : t(header.localizationSlug),
    );
  }

  getHeaders() {
    const { updateAction, t } = this.props;
    const localizationSlug =
      Object.keys(massUpdateActions).find(
        (key) => massUpdateActions[key] === updateAction,
      ) || '';
    const defaultColumnHeadersLocalization = defaultColumnHeaders.map(
      (header) =>
        header.count
          ? t(header.localizationSlug, { count: header.count })
          : t(header.localizationSlug),
    );
    const bulkClearTagsHeadersLocalization =
      this.getLocalisedHeaders(bulkClearTagsHeaders);
    const bulkAddTagsHeadersLocalization =
      this.getLocalisedHeaders(bulkAddTagsHeaders);
    switch (updateAction) {
      case massUpdateActions.SHIPMENT_ARRIVED:
      case massUpdateActions.EDIT_ESTIMATED_ARRIVAL:
      case massUpdateActions.EDIT_ARRIVAL:
      case massUpdateActions.EDIT_DISCHARGE:
      case massUpdateActions.EDIT_LAST_FREE_DAY:
      case massUpdateActions.EDIT_LFD_AT_RAIL_FACILITY:
      case massUpdateActions.EDIT_ETA_TO_FINAL_RAIL_FACILITY:
      case massUpdateActions.BULK_APPLY_ADMIN_NOTES:
      case massUpdateActions.UPDATE_LAST_CUT:
      case massUpdateActions.UPDATE_EARLIEST_RETURN:
      case massUpdateActions.UPDATE_EARLIEST_GET_EMPTY:
      case massUpdateActions.UPDATE_EMPTY_RETURN:
      case massUpdateActions.UPDATE_REVIEW_DATE:
      case massUpdateActions.UPDATE_ACCOUNTING_REVIEW_DATE:
        return [
          ...defaultColumnHeadersLocalization,
          titleize(t(localizationSlug)),
          t('APPLY_TO_ALL'),
          titleize(t('REMOVE')),
        ];
      case massUpdateActions.BULK_CLEAR_TAGS:
        return bulkClearTagsHeadersLocalization;
      case massUpdateActions.BULK_ADD_TAGS:
        return bulkAddTagsHeadersLocalization;
      default:
        return [...defaultColumnHeadersLocalization];
    }
  }

  getValidationClass(shipment, field) {
    const { status } = shipment;

    if (status[field]) {
      return {
        error: true,
      };
    }
    return {
      error: false,
    };
  }

  handleRemoveShipment = (shipment) => () =>
    this.props.onRemoveShipment(shipment);

  handleClearAll = () => this.props.onClearAll();

  handleApplyToAll = (shipmentId) => () => this.props.onApplyToAll(shipmentId);

  handleSaveAll = () => this.props.onSaveAll();

  handleDateFieldChange = (jobId, field) => (momentDateObj) => {
    const dateString = momentDateObj ? momentDateObj.toISOString() : null;
    this.props.onFieldChange(jobId, field, dateString);
  };

  handleTextChange = (jobId) => (inputString) => {
    this.setState({ notes: { [jobId]: inputString } });
  };

  showClearAll() {
    const { updateAction } = this.props;
    return !hideClearAllActions.includes(updateAction);
  }

  renderColumns(shipment) {
    const { updateAction } = this.props;

    switch (updateAction) {
      case massUpdateActions.SHIPMENT_ARRIVED:
      case massUpdateActions.EDIT_ESTIMATED_ARRIVAL:
      case massUpdateActions.EDIT_ARRIVAL:
      case massUpdateActions.EDIT_DISCHARGE:
      case massUpdateActions.EDIT_LAST_FREE_DAY:
      case massUpdateActions.EDIT_ETA_TO_FINAL_RAIL_FACILITY:
      case massUpdateActions.EDIT_LFD_AT_RAIL_FACILITY:
      case massUpdateActions.UPDATE_LAST_CUT:
      case massUpdateActions.UPDATE_EARLIEST_RETURN:
      case massUpdateActions.UPDATE_REVIEW_DATE:
      case massUpdateActions.UPDATE_EMPTY_RETURN:
      case massUpdateActions.UPDATE_ACCOUNTING_REVIEW_DATE:
      case massUpdateActions.UPDATE_EARLIEST_GET_EMPTY: {
        const field = getUpdateFieldForAction(updateAction);
        return this.renderDateShipmentColumns(shipment, field);
      }
      case massUpdateActions.BULK_APPLY_ADMIN_NOTES: {
        const field = getUpdateFieldForAction(updateAction);
        return this.renderTextInputColumns(shipment, field);
      }
      case massUpdateActions.BULK_CLEAR_TAGS: {
        return this.renderClearTagsColumns(shipment);
      }
      case massUpdateActions.BULK_ADD_TAGS: {
        return this.renderAddTagsColumns(shipment);
      }

      default:
        return renderDefaultColumns(shipment);
    }
  }

  renderDateShipmentColumns(shipment, field) {
    return [
      ...renderDefaultColumns(shipment),
      this.renderDateInput(shipment, field),
      this.renderApplyToAllButton(shipment),
      this.renderRemoveShipmentButton(shipment),
    ];
  }

  renderTextInputColumns(shipment, field) {
    return [
      ...renderDefaultColumns(shipment),
      this.renderTextInput(shipment, field),
      this.renderApplyToAllButton(shipment),
      this.renderRemoveShipmentButton(shipment),
    ];
  }

  renderAddBulkTags(shipment, field) {
    const { _id: jobId } = shipment;
    const { shipmentUpdates, shipmentUpdateResults } = this.props;
    const newTagsValue = shipmentUpdates?.[jobId]?.[field] || [];
    const emptyFields =
      shipmentUpdateResults?.flatMap((result) => result.job_ids) || [];
    const isEmpty = emptyFields.includes(jobId);
    return (
      <td key={`td-tag-editor-${jobId}`}>
        <TagEditor
          key={`Tag-Editor-${jobId}`}
          tags={newTagsValue}
          handleChange={(shipment, tags) => {
            const { _id: jobId } = shipment;
            this.props.onFieldChange(jobId, field, tags);
          }}
          object={shipment}
          suggestions={AdminConstants.shipmentTagSuggestions}
          wrapperClasses={isEmpty ? 'border-2 border-red-500 rounded-md' : ''}
        />
      </td>
    );
  }

  renderShipmentNumber(shipment) {
    const { _id: id } = shipment;
    const idKey = `id_${id}`;
    const shipmentLink = `/shipments/${id}`;
    return [
      <td key={idKey}>
        <a href={shipmentLink} target="_blank" rel="noreferrer">
          {shipment.shipment_id}
        </a>
      </td>,
    ];
  }

  renderShipperName(shipment) {
    const { _id: id } = shipment;
    const shipperNameKey = `shipper_${id}`;
    return [<td key={shipperNameKey}>{shipment.shipper_name}</td>];
  }

  renderShipmentTags(shipment, field) {
    const { _id: id } = shipment;
    const tags = shipment?.[field]?.filter((tag) => tag) || [];
    return (
      <td>
        <div className="flex flex-wrap gap-2">
          {tags.map((tag, tag_id) => (
            <div
              key={`${id}_${tag_id}`}
              className="px-2 py-1 bg-gray-200 rounded text-sm whitespace-nowrap"
            >
              {tag}
            </div>
          ))}
        </div>
      </td>
    );
  }

  renderClearTagsColumns(shipment) {
    return [
      this.renderShipmentNumber(shipment),
      this.renderShipperName(shipment),
      this.renderShipmentTags(shipment, 'tags'),
      this.renderRemoveShipmentButton(shipment),
    ];
  }

  renderAddTagsColumns(shipment) {
    return [
      this.renderShipmentNumber(shipment),
      this.renderShipperName(shipment),
      this.renderShipmentTags(shipment, 'tags'),
      this.renderAddBulkTags(shipment, 'newTags'),
      this.renderApplyToAllButton(shipment, 'newTags'),
      this.renderRemoveShipmentButton(shipment),
    ];
  }

  renderTextInput(shipment, field) {
    const { shipmentUpdates } = this.props;
    const selectedNote =
      shipmentUpdates[shipment._id] && shipmentUpdates[shipment._id][field];

    const noteKey = `note_${shipment._id}_${selectedNote}`;
    const className = {
      'md:w-1/6': true,
      ...this.getValidationClass(shipment, 'text'),
    };

    return (
      <td key={noteKey} className={classnames(className)}>
        <div>
          <CargomaticInput
            type="textarea"
            key={noteKey}
            onBlur={() =>
              this.props.onFieldChange(
                shipment._id,
                field,
                this.state.notes[shipment._id],
              )
            }
            defaultValue={selectedNote}
            value={
              typeof this.state.notes[shipment._id] === 'string'
                ? this.state.notes[shipment._id]
                : selectedNote
            }
            placeholderText={field}
            onChange={this.handleTextChange(shipment._id)}
          />
        </div>
      </td>
    );
  }

  renderDateInput(shipment, field) {
    const { shipmentUpdates, t } = this.props;
    const selectedDate =
      shipmentUpdates[shipment._id] && shipmentUpdates[shipment._id][field];
    const datePickerKey = `datePicker_${shipment._id}_${selectedDate}`;
    const className = {
      ...this.getValidationClass(shipment, 'date'),
    };
    const parts = selectedDate ? selectedDate.split('-') : [];
    const date =
      parts.length > 0
        ? new Date(parts[0], parts[1] - 1, parts[2].substring(0, 2))
        : null;

    return (
      <td key={datePickerKey} className={classnames(className)}>
        <div>
          <DatePicker
            key={datePickerKey}
            selected={mapDateValue(selectedDate)}
            placeholderText={t(sluggify(humanize(field)))}
            className="w-full shadow border rounded px-3 -ml-3"
            showTimeSelect
            dateFormat="MM/DD/YYYY hh:mm a"
            timeIntervals={30}
            onChange={this.handleDateFieldChange(shipment._id, field)}
          />
        </div>
      </td>
    );
  }

  getDisableApplyToAll(shipment, field) {
    let disableApplyToAll = false;
    const { _id: jobId } = shipment;
    const { shipmentUpdates, action, shipments } = this.props;
    const lastShipment = shipments[shipments.length - 1];
    const shipmentUpdate = shipmentUpdates?.[jobId];
    if (action === massUpdateActions.BULK_ADD_TAGS) {
      disableApplyToAll = !shipmentUpdate?.[field]?.length;
    }
    if (
      disableApplyToAllForLast.includes(action) &&
      lastShipment._id === jobId
    ) {
      disableApplyToAll = true;
    }
    return disableApplyToAll;
  }

  renderApplyToAllButton(shipment, field) {
    const applyAllKey = `apply_${shipment._id}`;
    const { t } = this.props;
    const buttonClassName = {
      button: true,
      'button-hollow-green': true,
    };
    const disableApplyToAll = this.getDisableApplyToAll(shipment, field);
    return (
      <td key={applyAllKey}>
        <button
          type="button"
          className={classnames(buttonClassName)}
          onClick={this.handleApplyToAll(shipment._id, field)}
          disabled={disableApplyToAll}
        >
          {titleize(t('APPLY'))}
        </button>
      </td>
    );
  }

  renderRemoveShipmentButton(shipment) {
    const removeShipmentKey = `remove_${shipment._id}`;
    return (
      <td key={removeShipmentKey}>
        <button
          className="button button-secondary"
          onClick={this.handleRemoveShipment(shipment)}
        >
          <i className="fa fa-remove" />
        </button>
      </td>
    );
  }

  renderShipment(shipment) {
    const columns = this.renderColumns(shipment);
    return (
      <tr key={shipment._id} className="p-3">
        {columns}
      </tr>
    );
  }

  renderHeader() {
    const columnHeaders = this.getHeaders();
    return (
      <thead>
        <tr>
          {columnHeaders.map((col) => (
            <th key={col} className="font-bold text-grey-darker">
              {col}
            </th>
          ))}
        </tr>
      </thead>
    );
  }

  renderBody() {
    const { shipments } = this.props;

    return (
      <tbody>
        {shipments.map((shipment) => this.renderShipment(shipment))}
      </tbody>
    );
  }

  renderStatuses() {
    const { shipmentUpdateResults } = this.props;

    if (!shipmentUpdateResults || shipmentUpdateResults.length === 0) {
      return null;
    }

    return (
      <div className="shipment-status-wrapper flex flex-col mr-4">
        {shipmentUpdateResults.map((s) => renderStatus(s))}
      </div>
    );
  }

  render() {
    const { title, saving, disableSave, t, disableClearAll } = this.props;

    const buttonDisabled = saving || disableSave;
    return (
      <div className="shadow bg-white p-4 mb-6">
        <div className="flex justify-between">
          <h4 className="mb-2 text-grey-darker">{title}</h4>
        </div>
        <hr />
        <div className="shipment-rows">
          <table className="table">
            {this.renderHeader()}
            {this.renderBody()}
          </table>
          <div className="flex justify-end items-baseline">
            {this.renderStatuses()}
            {this.showClearAll() && (
              <button
                className="button button-hollow-green mr-2"
                onClick={this.handleClearAll}
                disabled={disableClearAll}
              >
                {t('CLEAR_ALL')}
              </button>
            )}
            <button
              className={`button button-green flex items-center unifiedView-masterList-${this.props.updateAction} heap`}
              disabled={buttonDisabled}
              onClick={this.handleSaveAll}
            >
              {saving && (
                <div className="flex items-center mr-2">
                  <i className="fa fa-spinner fa-spin" />
                </div>
              )}
              <span>{this.getActionName()}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }
}

function renderStatus(status) {
  switch (status.type) {
    case 'error':
      return (
        <ErrorAlert key={status.type} title={status.message}>
          {status.items.length && (
            <ul>
              {status.items.map((id) => (
                <li key={id}>{id}</li>
              ))}
            </ul>
          )}
        </ErrorAlert>
      );
    case 'success':
      return (
        <div className="border-solid border-l-4 border-green bg-green-lightest text-green-darker mb-4 p-4">
          <p className="font-bold mb-2">{status.message}</p>
          {status.items.length && (
            <ul>
              {status.items.map((id) => (
                <li key={id}>{id}</li>
              ))}
            </ul>
          )}
        </div>
      );

    default:
      return null;
  }
}

function formatEquipments(shipment) {
  const equipments = shipment.stops[0].equipments || [];
  if (equipments.length === 0) {
    return t('NONE');
  }

  return (
    <ul>
      {equipments.map((e) => (
        <li className="mb-2" key={e._id}>
          {e.equipment_id}
        </li>
      ))}
    </ul>
  );
}

function mapDateValue(value) {
  if (!value) {
    return null;
  }

  if (value instanceof moment) {
    return value;
  }

  return moment(value);
}

function renderDefaultColumns(shipment) {
  const { _id: id } = shipment;

  const idKey = `id_${id}`;
  const shipperNameKey = `shipper_${id}`;
  const equipmentsKey = `equipments_${id}`;
  const shipmentLink = `/shipments/${id}`;

  return [
    <td key={idKey}>
      <a href={shipmentLink} target="_blank" rel="noreferrer">
        {shipment.shipment_id}
      </a>
    </td>,
    <td key={shipperNameKey}>{shipment.shipper_name}</td>,
    <td key={equipmentsKey}>{formatEquipments(shipment)}</td>,
  ];
}

export default withTranslation()(ShipmentsGroup);
